import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from '@phosphor-icons/react/dist/ssr/Info';
import {
  QUEST_DIFFICULTY_GUIDE,
  type QuestDifficulty,
  type ResolvedDifficultyPricing,
} from '@aegislab/shared';
import { IconButton } from '@/shared/ui/atoms';
import * as s from './DifficultyPricingInfo.css';

export interface DifficultyPricingView extends ResolvedDifficultyPricing {
  /** 접근 이름이자 덮개 제목 — 「개발팀 단가표」. 팀 없는 퀘스트는 「회사 기본 단가표」. */
  title: string;
  /** 이 퀘스트(또는 지금 고른) 등급 — 그 행을 표시한다. */
  difficulty?: QuestDifficulty | null;
  /** 표 위 한 문장 — 표가 없는 팀이면 회사 범위를 쓴다고 알린다. */
  note?: string;
}

const GAP = 4;
const fmt = (n: number) => n.toLocaleString();

/**
 * 단가표 ⓘ — 금액 옆에서 그 금액의 기준 표를 연다(퀘스트 540e5d83, cp1-decision §4).
 * 선례는 팀 운영 「오늘 현황」 카드의 「집계 기준 보기」(TeamOpsRail) — 숫자 옆 i 버튼이 설명을 펼친다.
 *
 * 덮개는 body 로 포털해 fixed 로 띄운다(ChipPopover 와 같은 방식). 버튼이 범위 문장·보상 줄의 `<p>`
 * 안에 서서 표를 그 자리에 그릴 수 없고, 패널 본문이 스크롤 영역이라 안에 그리면 잘린다.
 * 예산·배당·개인 지급은 싣지 않는다.
 */
export function DifficultyPricingInfo({
  title,
  difficulty,
  note,
  ranges,
  anchors,
}: DifficultyPricingView) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const titleId = useId();

  // 닫으면 버튼으로 포커스를 돌려준다 — 처음 마운트 때는 건너뛴다.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !open) buttonRef.current?.focus();
    wasOpen.current = open;
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const b = buttonRef.current?.getBoundingClientRect();
      const p = panelRef.current;
      if (!b || !p) return;
      const h = p.offsetHeight;
      const w = p.offsetWidth;
      const below = window.innerHeight - b.bottom - GAP;
      const top = below >= h || b.top < h ? b.bottom + GAP : b.top - h - GAP;
      const left = Math.max(8, Math.min(b.right - w, window.innerWidth - w - 8));
      setPos({ top, left });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!buttonRef.current?.contains(t) && !panelRef.current?.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <IconButton
        ref={buttonRef}
        size="xs"
        className={s.trigger}
        icon={<Info weight="bold" />}
        aria-label={title}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          setPos(null);
          setOpen((v) => !v);
        }}
      />
      {open &&
        createPortal(
          <div
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-labelledby={titleId}
            className={s.panel}
            style={{
              top: pos?.top ?? 0,
              left: pos?.left ?? 0,
              visibility: pos ? 'visible' : 'hidden',
            }}
          >
            <p id={titleId} className={s.title}>
              {title}
            </p>
            {note && <p className={s.note}>{note}</p>}
            <table className={s.table}>
              <thead>
                <tr>
                  <th scope="col" className={s.levelHead}>
                    난이도
                  </th>
                  <th scope="col" className={s.headCell}>
                    범위 (P)
                  </th>
                  <th scope="col" className={s.headCell}>
                    기준값 (P)
                  </th>
                </tr>
              </thead>
              <tbody>
                {QUEST_DIFFICULTY_GUIDE.map((level) => {
                  const range = ranges[level.value];
                  const anchor = anchors[level.value];
                  const current = level.value === difficulty;
                  return (
                    <tr
                      key={level.value}
                      className={current ? s.currentRow : undefined}
                      aria-current={current ? 'true' : undefined}
                    >
                      {/* 등급 이름은 MCP 가 쓰는 코드(COMMON…)로 쓴다(박인혜 2026-10-13) */}
                      <th scope="row" className={`${s.levelCell} ${s.levelColor[level.value]}`}>
                        {level.value}
                      </th>
                      <td className={s.cell}>{`${fmt(range.min)}–${fmt(range.max)}`}</td>
                      <td className={s.cell}>{anchor !== undefined ? fmt(anchor) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className={s.footer}>범위 밖 금액으로는 만들 수 없습니다.</p>
          </div>,
          document.body
        )}
    </>
  );
}
